import { useState, useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { useAuth } from "@/hooks/useAuth";
import { useClientBilling, useAgencyBilling } from "@/hooks/useAgencyBilling";
import { useToast } from "@/hooks/use-toast";
import { AgencyUsageChart } from "@/components/agency/AgencyUsageChart";
import { MarkupConfigDialog } from "@/components/agency/MarkupConfigDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Invoice, BillingFilters } from "@/types/billing";
import {
  ArrowLeft,
  Settings,
  FileText,
  DollarSign,
  Phone,
  MessageSquare,
  Bot,
  Download,
  Eye,
} from "lucide-react";
import { exportInvoicesCSV } from "@/lib/billing-export";

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInvoiceStatusBadge = (status: Invoice["status"]) => {
  switch (status) {
    case "paid":
      return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
    case "sent":
      return <Badge className="bg-blue-100 text-blue-800">Sent</Badge>;
    case "draft":
      return <Badge variant="secondary">Draft</Badge>;
    case "overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "cancelled":
      return <Badge variant="outline">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export default function ClientBillingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAgency, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);

  // Fetch client billing data
  const { client, invoices, isLoading, error } = useClientBilling(id || null);

  // Fetch usage chart and markup configs
  const filters: BillingFilters = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    return {
      period_start: start.toISOString().split("T")[0],
      period_end: now.toISOString().split("T")[0],
      tenant_id: id,
    };
  }, [id]);

  const {
    usageChart,
    markupConfigs,
    updateMarkup,
    isUpdatingMarkup,
    generateInvoice,
    isGeneratingInvoice,
  } = useAgencyBilling(filters);

  // Get markup config for this client
  const clientMarkupConfig = useMemo(() => {
    return markupConfigs.find((c) => c.tenant_id === id) || null;
  }, [markupConfigs, id]);

  const defaultMarkup = useMemo(() => {
    const defaultConfig = markupConfigs.find((c) => c.tenant_id === null);
    return defaultConfig?.markup_percentage ?? 20;
  }, [markupConfigs]);

  // Handle markup save
  const handleSaveMarkup = (config: Parameters<typeof updateMarkup>[0]) => {
    updateMarkup(config, {
      onSuccess: () => {
        toast({
          title: "Markup Updated",
          description: `Markup for ${client?.tenant_name} has been updated.`,
        });
        setMarkupDialogOpen(false);
      },
      onError: (error: Error) => {
        toast({
          title: "Failed to update markup",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  // Handle invoice generation
  const handleGenerateInvoice = () => {
    if (!client) return;

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    generateInvoice(
      {
        tenant_id: client.tenant_id,
        period_start: start.toISOString().split("T")[0],
        period_end: now.toISOString().split("T")[0],
      },
      {
        onSuccess: () => {
          toast({
            title: "Invoice Generated",
            description: `Invoice for ${client.tenant_name} has been created.`,
          });
        },
        onError: (error: Error) => {
          toast({
            title: "Failed to generate invoice",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  // Handle export invoices
  const handleExportInvoices = () => {
    if (!client || invoices.length === 0) return;
    exportInvoicesCSV(invoices, client.tenant_name);
    toast({
      title: "Export Complete",
      description: `Invoice history for ${client.tenant_name} has been downloaded.`,
    });
  };

  // Redirect if not an agency
  if (!authLoading && !isAgency()) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <Skeleton className="h-8 w-48" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Skeleton className="h-[300px] w-full" />
        </div>
      </MainLayout>
    );
  }

  if (error || !client) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate("/agency/billing")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Billing
          </Button>
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">
                {error ? `Error loading client: ${(error as Error).message}` : "Client not found"}
              </p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const usage = client.usage_summary;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/agency/billing")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{client.tenant_name}</h1>
              <p className="text-muted-foreground">Client Billing Details</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setMarkupDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configure Markup
            </Button>
            <Button
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || client.unbilled_amount <= 0}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Invoice
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Billed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(client.total_billed)}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(client.base_cost)} + {client.markup_percentage}% markup
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-600">Markup Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(client.markup_amount)}
              </div>
              <p className="text-xs text-muted-foreground">{client.markup_percentage}% of base</p>
            </CardContent>
          </Card>

          <Card className={client.unbilled_amount > 0 ? "border-amber-200" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Unbilled Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${
                  client.unbilled_amount > 0 ? "text-amber-600" : ""
                }`}
              >
                {formatCurrency(client.unbilled_amount)}
              </div>
              <p className="text-xs text-muted-foreground">
                {client.unbilled_amount > 0 ? "Ready for invoicing" : "All caught up"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Last Invoice</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatDate(client.last_invoice_date)}</div>
              <p className="text-xs text-muted-foreground">
                Status:{" "}
                {client.status === "current"
                  ? "Current"
                  : client.status === "overdue"
                    ? "Overdue"
                    : "Pending"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Usage Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Usage Breakdown</CardTitle>
            <CardDescription>Current period usage by service type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Phone className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Voice Calls</p>
                  <p className="font-medium">{usage.voice_minutes.toLocaleString()} min</p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(usage.voice_cost)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <MessageSquare className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">SMS Messages</p>
                  <p className="font-medium">{usage.sms_count.toLocaleString()} msgs</p>
                  <p className="text-sm text-muted-foreground">{formatCurrency(usage.sms_cost)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <FileText className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Transcription</p>
                  <p className="font-medium">
                    {(usage.transcription_chars / 1000).toFixed(1)}k chars
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(usage.transcription_cost)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Bot className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="text-sm text-muted-foreground">AI Analysis</p>
                  <p className="font-medium">{usage.ai_requests.toLocaleString()} requests</p>
                  <p className="text-sm text-muted-foreground">{formatCurrency(usage.ai_cost)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage Chart */}
        <AgencyUsageChart
          data={usageChart}
          isLoading={false}
          title="Usage History"
          description="Usage costs over the last 3 months"
        />

        {/* Invoice History */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Invoice History</CardTitle>
              <CardDescription>Past invoices for this client</CardDescription>
            </div>
            {invoices.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExportInvoices}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No invoices yet</p>
                <p className="text-sm">Generate an invoice to see it here.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">Markup</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell>
                        {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(invoice.base_amount)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        +{formatCurrency(invoice.markup_amount)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.amount)}
                      </TableCell>
                      <TableCell>{getInvoiceStatusBadge(invoice.status)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/agency/billing/invoices/${invoice.id}`)}
                            title="View Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Download Invoice">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Markup Configuration Dialog */}
        <MarkupConfigDialog
          open={markupDialogOpen}
          onOpenChange={setMarkupDialogOpen}
          client={client}
          existingConfig={clientMarkupConfig}
          defaultMarkup={defaultMarkup}
          onSave={handleSaveMarkup}
          isSaving={isUpdatingMarkup}
        />
      </div>
    </MainLayout>
  );
}
